import { ReactNode } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'

import { useAuth } from 'auth/AuthContext'
import { AppLayout, ProjectLayout } from 'components/Layout'
import { Spinner } from 'components/ui'
import { ApprovalsPage } from 'pages/ApprovalsPage'
import { AttachmentsPage } from 'pages/AttachmentsPage'
import { DashboardPage } from 'pages/DashboardPage'
import { AcceptInvitationPage } from 'pages/auth/AcceptInvitationPage'
import { ForgotPasswordPage } from 'pages/auth/ForgotPasswordPage'
import { OtpVerificationPage } from 'pages/auth/OtpVerificationPage'
import { SelectProjectPage } from 'pages/auth/SelectProjectPage'
import { SignInPage } from 'pages/auth/SignInPage'
import { AnalyticsPortfolioPage } from 'pages/AnalyticsPortfolioPage'
import { CrewPage } from 'pages/CrewPage'
import { CrewTradesPage } from 'pages/CrewTradesPage'
import { DocumentsPage } from 'pages/DocumentsPage'
import { ExportsPage } from 'pages/ExportsPage'
import { EquipmentCataloguePage } from 'pages/EquipmentCataloguePage'
import { EquipmentFleetPage } from 'pages/EquipmentFleetPage'
import { IssuesPage } from 'pages/IssuesPage'
import { MaterialsCataloguePage } from 'pages/MaterialsCataloguePage'
import { MaterialsRegistryPage } from 'pages/MaterialsRegistryPage'
import { NewProjectPage } from 'pages/NewProjectPage'
import { ProjectIndexPage } from 'pages/ProjectIndexPage'
import { PortfolioCalendarPage } from 'pages/PortfolioCalendarPage'
import { ProjectsPage } from 'pages/ProjectsPage'
import { PrintPreviewPage } from 'pages/report/PrintPreviewPage'
import { ReportPage } from 'pages/report/ReportPage'
import { SettingsPage } from 'pages/SettingsPage'
import { SiteOverviewPage } from 'pages/SiteOverviewPage'
import { SitesPage } from 'pages/SitesPage'
import { SummariesPage } from 'pages/SummariesPage'
import { TodayPage } from 'pages/TodayPage'
import { WorkAreasPage } from 'pages/WorkAreasPage'
import { NotFoundPage } from 'pages/system/NotFoundPage'
import { InvitationsPage } from 'pages/InvitationsPage'
import { UsersPage } from 'pages/UsersPage'
import { ProfilePage } from 'pages/ProfilePage'
import { RolesPage } from 'pages/RolesPage'
import { AppearanceSettingsPage } from 'pages/settings/AppearanceSettingsPage'
import { GeneralSettingsPage } from 'pages/settings/GeneralSettingsPage'
import { LanguageSettingsPage } from 'pages/settings/LanguageSettingsPage'
import { NotificationSettingsPage } from 'pages/settings/NotificationSettingsPage'
import { SecuritySettingsPage } from 'pages/settings/SecuritySettingsPage'

const RequireAuth = ({ children }: { children: ReactNode }) => {
  const { user, loading } = useAuth()
  const location = useLocation()
  if (loading) return <Spinner />
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />
  return <>{children}</>
}

export const App = () => (
  <Routes>
    <Route path="/login" element={<SignInPage />} />
    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
    <Route path="/otp" element={<OtpVerificationPage />} />
    <Route path="/accept-invite" element={<AcceptInvitationPage />} />
    <Route
      path="/projects/:projectId/reports/:reportId/print"
      element={
        <RequireAuth>
          <PrintPreviewPage />
        </RequireAuth>
      }
    />
    <Route
      path="/select-project"
      element={
        <RequireAuth>
          <SelectProjectPage />
        </RequireAuth>
      }
    />
    <Route
      element={
        <RequireAuth>
          <AppLayout />
        </RequireAuth>
      }
    >
      <Route index element={<ProjectsPage />} />
      <Route path="dashboard" element={<DashboardPage />} />
      <Route path="approvals" element={<ApprovalsPage />} />
      <Route path="sites" element={<SitesPage />} />
      <Route path="calendar" element={<PortfolioCalendarPage />} />
      <Route path="equipment" element={<EquipmentFleetPage />} />
      <Route path="materials" element={<MaterialsRegistryPage />} />
      <Route path="crew" element={<CrewPage />} />
      <Route path="analytics" element={<AnalyticsPortfolioPage />} />
      <Route path="exports" element={<ExportsPage />} />
      <Route path="profile" element={<ProfilePage />} />
      <Route path="admin/users" element={<UsersPage />} />
      <Route path="admin/roles" element={<RolesPage />} />
      <Route path="admin/invitations" element={<InvitationsPage />} />
      <Route path="admin/settings" element={<Navigate to="/admin/settings/general" replace />} />
      <Route path="admin/settings/general" element={<GeneralSettingsPage />} />
      <Route path="admin/settings/language" element={<LanguageSettingsPage />} />
      <Route path="admin/settings/appearance" element={<AppearanceSettingsPage />} />
      <Route path="admin/settings/notifications" element={<NotificationSettingsPage />} />
      <Route path="admin/settings/security" element={<SecuritySettingsPage />} />
      <Route path="projects/new" element={<NewProjectPage />} />
      <Route path="projects/:projectId" element={<ProjectLayout />}>
        <Route index element={<ProjectIndexPage />} />
        <Route path="overview" element={<SiteOverviewPage />} />
        <Route path="work-areas" element={<WorkAreasPage />} />
        <Route path="issues" element={<IssuesPage />} />
        <Route path="documents" element={<DocumentsPage />} />
        <Route path="today" element={<TodayPage />} />
        <Route path="reports/:reportId" element={<ReportPage />} />
        <Route path="attachments" element={<AttachmentsPage />} />
        <Route path="summaries" element={<SummariesPage />} />
        <Route path="equipment" element={<EquipmentCataloguePage />} />
        <Route path="materials" element={<MaterialsCataloguePage />} />
        <Route path="crew" element={<CrewTradesPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>
      <Route path="*" element={<NotFoundPage />} />
    </Route>
  </Routes>
)
